/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState } from 'react';
import { Check, ImageOff, Upload, X } from 'lucide-react';
import { api } from '../lib/api';
import { unreadableAnalysis, type BulkFileAnalysis } from '../lib/bulkOutfits';
import { bytesToBase64, formatBytes, mascotErrorReloads, type AdminOutfit } from '../lib/desktopMascot';
import {
  REPLACE_PICTURE_HINT,
  confirmPictureText,
  pictureSavedAnyway,
  replacePictureBody,
  replacePictureErrorText,
  replacedPictureText,
} from '../lib/outfitPicture';
import { Button, Callout, Icon } from '../ui';
import { analyzeOutfitFile } from './mascotCanvas';
import { wallpaperClass, type Wallpaper } from './mascotUi';
import { SaveNote } from './SaveNote';

export interface OutfitPictureReplaceProps {
  /** The selected gallery outfit (the card keys this action by its id, so a chosen file never follows another outfit). */
  outfit: Pick<AdminOutfit, 'id' | 'name' | 'placement'>;
  /** The catalog revision the card loaded, and the annex revision (sent for an outfit kept beyond the catalog). */
  revision: number;
  annexRevision: number | null | undefined;
  /** The outfits the card knows (the gallery's, the retired ones, the unfinished ones), so a duplicate is named. */
  known: ReadonlyArray<Pick<AdminOutfit, 'id' | 'name'>>;
  /** Writes are off: an auditor, another change in progress, or no catalog. */
  disabled: boolean;
  wallpaper: Wallpaper;
  /** While the picture is sent, so the card holds its other changes. */
  onBusy: (busy: boolean) => void;
  /** Read the catalog again after the picture changed (or may have: another admin's change, a queued audit record). */
  reload: () => Promise<unknown>;
  /** How the chosen file is checked and prepared (the add's canvas work, analyzeOutfitFile); the render tests pass their own. */
  analyze?: (file: File, mirror: boolean) => Promise<BulkFileAnalysis>;
}

interface Chosen {
  fileName: string;
  analysis: BulkFileAnalysis;
}

/**
 * Replace picture, on the selected outfit (owner, 2026-11-01: "a small Gyro Admin feature that swaps an outfit's picture
 * in place; reusable for future art fixes"): choose a PNG, which is checked and prepared for desktops as an added outfit's
 * is (analyzeOutfitFile, never mirrored here: the service mirrors one drawn facing right, as at the add), see it, then
 * Confirm sends it (PUT …/outfits/:id/image) with the revisions the card loaded, or Cancel lets it go. The note says
 * what happened in plain words; the card reads the catalog again when the picture changed.
 */
export function OutfitPictureReplace({ outfit, revision, annexRevision, known, disabled, wallpaper, onBusy, reload, analyze = analyzeOutfitFile }: OutfitPictureReplaceProps) {
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const token = useRef(0);

  // The preview's picture is let go when it is replaced and when the action goes away.
  useEffect(() => () => {
    if (chosen?.analysis.thumbUrl) URL.revokeObjectURL(chosen.analysis.thumbUrl);
  }, [chosen]);

  const choose = async (file: File) => {
    const mine = (token.current += 1);
    setChecking(true);
    setChosen(null);
    setNote(null);
    let analysis: BulkFileAnalysis;
    try {
      analysis = await analyze(file, false);
    } catch {
      analysis = unreadableAnalysis(file.size);
    }
    if (mine !== token.current) {
      // Another file was chosen meanwhile, or Cancel was pressed.
      if (analysis.thumbUrl) URL.revokeObjectURL(analysis.thumbUrl);
      return;
    }
    setChosen({ fileName: file.name, analysis });
    setChecking(false);
  };

  const cancel = () => {
    token.current += 1;
    setChecking(false);
    setChosen(null);
  };

  const confirm = async () => {
    const upload = chosen?.analysis.upload;
    if (!chosen || !upload || chosen.analysis.refusal !== null) return;
    setSending(true);
    onBusy(true);
    setNote(null);
    let changed = false;
    try {
      const pngBase64 = bytesToBase64(new Uint8Array(await upload.arrayBuffer()));
      const answer = await api.replaceDesktopMascotOutfitImage(outfit.id, replacePictureBody(pngBase64, outfit, revision, annexRevision));
      changed = answer.changed;
      setChosen(null);
      setNote({ ok: true, text: replacedPictureText(outfit.name, answer) });
    } catch (error) {
      // Saved with a caveat (its audit record queued or refused, its facing not saved): the form is done.
      if (pictureSavedAnyway(error)) setChosen(null);
      changed = pictureSavedAnyway(error) || mascotErrorReloads(error);
      setNote({ ok: false, text: replacePictureErrorText(error, known) });
    } finally {
      setSending(false);
      onBusy(false);
    }
    if (changed) await reload();
  };

  const analysis = chosen?.analysis ?? null;
  const ready = analysis !== null && analysis.refusal === null && analysis.upload !== null;
  return (
    <div className="ui-form" data-testid="replace-picture">
      <div className="ui-actions">
        <input
          ref={input}
          type="file"
          accept="image/png"
          hidden
          aria-label={`Choose a new picture for ${outfit.name}`}
          disabled={disabled || checking || sending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void choose(file);
          }}
        />
        <Button size="sm" icon={Upload} busy={checking} disabled={disabled || checking || sending} title={REPLACE_PICTURE_HINT} onClick={() => input.current?.click()}>
          Replace picture
        </Button>
      </div>
      {chosen && analysis && (
        <div className="ui-row-editor">
          <div className="ui-form">
            <div className="ui-actions">
              <span className={`ui-tile-pic ${wallpaperClass(wallpaper)}`}>
                {analysis.thumbUrl
                  ? <img src={analysis.thumbUrl} alt={`The new picture for ${outfit.name}`} className="ui-tile-img" />
                  : <Icon icon={ImageOff} size="md" className="ui-row-pic-none" />}
              </span>
              <span className="ui-cell-stack">
                <span>{chosen.fileName}</span>
                {analysis.width > 0 && <span className="ui-cell-sub">{analysis.width} x {analysis.height} px · {formatBytes(analysis.sizeBytes)}</span>}
                {analysis.prepared && <span className="ui-cell-sub">{analysis.prepared}</span>}
                {analysis.warnings.map((warning) => <span key={warning} className="ui-cell-sub">{warning}</span>)}
              </span>
            </div>
            {analysis.refusal !== null
              ? <Callout tone="bad" role="alert">{analysis.refusal} Nothing was sent.</Callout>
              : <span>{confirmPictureText(outfit.name)}</span>}
            <div className="ui-actions">
              {ready && (
                <Button variant="primary" size="sm" icon={Check} busy={sending} disabled={disabled || sending} onClick={() => void confirm()}>
                  Confirm
                </Button>
              )}
              <Button variant="ghost" size="sm" icon={X} disabled={sending} onClick={cancel}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
      <SaveNote note={note} />
    </div>
  );
}
